import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  cancelRideAsAdmin, completeRideAsAdmin, rebroadcastRideAsAdmin, formatTime, toDate,
  listenRideMessages, listenRideEvents, listenRideTimeline, phoneHref, waHref,
} from '../services/firebase';
import { downloadCsv } from '../utils/exportCsv';
import {
  ACTIVE_STATUSES, STATUS_CLASS, STATUS_LABEL, RANGE_OPTIONS, minutesAgo, rangeBounds, inBounds,
} from '../utils/rides';
import { buildRideTimeline, dispatchSteps, formatRadius } from '../utils/rideTimeline';
import { useLang } from '../lang/LangContext';
import RideTimelineList from '../components/RideTimelineList';

export default function Rides({ rides = [], drivers = [], initialSelectedId = null, onClearSelected, isSuperAdmin = false }) {
  const { t } = useLang();
  const [filter, setFilter] = useState('active');
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [q, setQ] = useState('');
  const [selectedId, setSelectedId] = useState(initialSelectedId);
  const [activeTab, setActiveTab] = useState('timeline'); // 'timeline' | 'dispatch' | 'events' | 'chat'
  const [busy, setBusy] = useState(null);
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    if (initialSelectedId) {
      setSelectedId(initialSelectedId);
      const targetRide = rides.find((r) => r.id === initialSelectedId);
      if (targetRide) {
        if (targetRide.status === 'completed') setFilter('completed');
        else if (targetRide.status === 'cancelled') setFilter('cancelled');
        else if (targetRide.status === 'searching') setFilter('searching');
        else if (ACTIVE_STATUSES.includes(targetRide.status)) setFilter('active');
        else setFilter('all');
      } else {
        setFilter('all');
      }
    }
  }, [initialSelectedId, rides]);

  // Deep-linked rides may be from any day; make sure the row isn't hidden by a date range.
  useEffect(() => {
    if (initialSelectedId) setRange('all');
  }, [initialSelectedId]);

  const bounds = useMemo(() => rangeBounds(range, customFrom, customTo), [range, customFrom, customTo]);

  const rangeRides = useMemo(() => {
    if (!bounds) return rides;
    return rides.filter((r) => inBounds(r.createdAt, bounds));
  }, [rides, bounds]);

  const summary = useMemo(() => {
    let completed = 0; let cancelled = 0; let active = 0; let revenue = 0;
    rangeRides.forEach((r) => {
      if (r.status === 'completed') {
        completed += 1;
        const f = Number(r.fare);
        if (Number.isFinite(f)) revenue += f;
      } else if (r.status === 'cancelled') cancelled += 1;
      else if (ACTIVE_STATUSES.includes(r.status)) active += 1;
    });
    return { total: rangeRides.length, completed, cancelled, active, revenue };
  }, [rangeRides]);

  // Derived live ride object (100% reactive to backend Firestore status updates while drawer is open)
  const selected = useMemo(() => {
    if (!selectedId) return null;
    return rides.find((r) => r.id === selectedId) || null;
  }, [selectedId, rides]);

  useEffect(() => {
    if (!selectedId) { setMessages([]); setEvents([]); setHistory([]); return undefined; }
    // Drop the previous ride's rows at once so one ride never shows another's history.
    setHistory([]);
    const u1 = listenRideMessages(selectedId, setMessages);
    const u2 = listenRideEvents(selectedId, setEvents);
    const u3 = listenRideTimeline(selectedId, setHistory);
    return () => { u1?.(); u2?.(); u3?.(); };
  }, [selectedId]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return rangeRides.filter((r) => {
      if (filter === 'active' && !ACTIVE_STATUSES.includes(r.status)) return false;
      if (filter === 'searching' && r.status !== 'searching') return false;
      if (filter === 'completed' && r.status !== 'completed') return false;
      if (filter === 'cancelled' && r.status !== 'cancelled') return false;
      if (!qq) return true;
      const hay = [
        r.id, r.customerName, r.customerPhone, r.driverName, r.driverPhone,
        r.vehicleNumber, r.pickup?.address, r.destination?.name, r.cancelReason,
      ].join(' ').toLowerCase();
      return hay.includes(qq);
    });
  }, [rangeRides, filter, q]);

  const exportRows = () => {
    const rows = [
      ['id', 'status', 'fare', 'passenger', 'phone', 'captain', 'vehicle', 'pickup', 'drop', 'created'],
      ...filtered.map((r) => [
        r.id, r.status, r.fare, r.customerName, r.customerPhone,
        r.driverName, r.vehicleNumber, r.pickup?.address, r.destination?.name,
        formatTime(r.createdAt),
      ]),
    ];
    const suffix = bounds ? `-${bounds[0]}${bounds[1] !== bounds[0] ? `_to_${bounds[1]}` : ''}` : '';
    downloadCsv(`rides-${filter}${suffix}.csv`, rows);
  };

  const cancel = async (ride) => {
    const reason = window.prompt('Cancel reason', 'Cancelled by admin') || 'Cancelled by admin';
    setBusy(ride.id);
    try {
      await cancelRideAsAdmin(ride.id, reason);
      toast.success('Ride cancelled');
      setSelectedId(null);
    } catch (e) {
      console.error(e);
      toast.error('Could not cancel');
    } finally {
      setBusy(null);
    }
  };

  // Unlike the Stuck Rides queue's Rebroadcast (which only appears once a
  // ride has gone quiet for 10+ min), this is reachable immediately for any
  // assigned/arrived ride — e.g. an admin watching the Live Map notices the
  // assigned Anna's dot is nowhere near the pickup pin and wants to act
  // right away rather than waiting out the staleness timer.
  const rebroadcast = async (ride) => {
    if (!window.confirm('Rebroadcast this ride? The current Anna will be unassigned and nearby Annas will be notified again.')) return;
    setBusy(ride.id);
    try {
      await rebroadcastRideAsAdmin(ride.id);
      toast.success('Rebroadcast to nearby captains');
    } catch (e) {
      console.error(e);
      toast.error('Could not rebroadcast');
    } finally {
      setBusy(null);
    }
  };

  const forceComplete = async (ride) => {
    if (!window.confirm('Force-complete this ride? Use only for stuck trips.')) return;
    setBusy(ride.id);
    try {
      await completeRideAsAdmin(ride.id);
      toast.success('Marked completed');
      setSelectedId(null);
    } catch (e) {
      console.error(e);
      toast.error('Could not complete');
    } finally {
      setBusy(null);
    }
  };

  // Ordered story of the ride: server-written history (survives an Anna cancel
  // and re-match) merged with what the ride document itself still knows.
  const timelineView = useMemo(
    () => buildRideTimeline(selected, history, events, drivers),
    [selected, history, events, drivers],
  );
  const dispatchLog = useMemo(() => dispatchSteps(timelineView.steps), [timelineView]);

  return (
    <>
      <div className="toolbar">
        <select value={range} onChange={(e) => setRange(e.target.value)} aria-label="Date range">
          {RANGE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{t(label)}</option>)}
        </select>
        {range === 'custom' && (
          <>
            <input type="date" style={{ minWidth: 0 }} value={customFrom} max={customTo || undefined} onChange={(e) => setCustomFrom(e.target.value)} aria-label="From date" />
            <span className="muted">→</span>
            <input type="date" style={{ minWidth: 0 }} value={customTo} min={customFrom || undefined} onChange={(e) => setCustomTo(e.target.value)} aria-label="To date" />
          </>
        )}
        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="active">{t('Active')} ({summary.active})</option>
          <option value="searching">{t('Searching')} ({rangeRides.filter((r) => r.status === 'searching').length})</option>
          <option value="all">{t('All Rides')} ({summary.total})</option>
          <option value="completed">{t('Completed')} ({summary.completed})</option>
          <option value="cancelled">{t('Cancelled')} ({summary.cancelled})</option>
        </select>
        <input
          placeholder={t('Search name, phone, vehicle, place…')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <span className="muted">{filtered.length} rides</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={exportRows}>{t('Export CSV')}</button>
      </div>

      <div className="stat-grid">
        <div className="stat-card blue">
          <div className="stat-label">{t('TOTAL RIDES')}</div>
          <div className="stat-value blue">{summary.total}</div>
          <div className="stat-sub">{summary.active} {t('in progress')}</div>
        </div>
        <div className="stat-card green">
          <div className="stat-label">{t('COMPLETED')}</div>
          <div className="stat-value green">{summary.completed}</div>
          <div className="stat-sub">
            {summary.total ? Math.round((summary.completed / summary.total) * 100) : 0}% {t('completion rate')}
          </div>
        </div>
        <div className="stat-card red">
          <div className="stat-label">{t('CANCELLED')}</div>
          <div className="stat-value red">{summary.cancelled}</div>
          <div className="stat-sub">
            {summary.total ? Math.round((summary.cancelled / summary.total) * 100) : 0}% {t('of rides')}
          </div>
        </div>
        <div className="stat-card orange">
          <div className="stat-label">{t('FARES (COMPLETED)')}</div>
          <div className="stat-value orange">₹{summary.revenue.toLocaleString('en-IN')}</div>
          <div className="stat-sub">
            {summary.completed ? `₹${Math.round(summary.revenue / summary.completed)} ${t('avg per ride')}` : '—'}
          </div>
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <div className="empty">{t('No rides match filter')}</div>
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                    <th>{t('STATUS')}</th>
                    <th>{t('ROUTE')}</th>
                    <th>{t('FARE')}</th>
                    <th>{t('PASSENGER')}</th>
                    <th>{t('CAPTAIN')}</th>
                    <th>{t('TIME')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const age = minutesAgo(toDate(r.createdAt));
                  const stuck = r.status === 'searching' && age >= 5;
                  return (
                    <tr key={r.id} className={stuck ? 'row-warn' : ''}>
                      <td>
                        <span className={`badge ${STATUS_CLASS[r.status] || 'gray'}`}>{STATUS_LABEL[r.status] || r.status}</span>
                        {stuck && <div className="muted">{age}m</div>}
                        {Array.isArray(r.cancelledDriverIds) && r.cancelledDriverIds.length > 0 && (
                          <div className="muted" title="Annas who accepted this ride and then cancelled">
                            ↩ {r.cancelledDriverIds.length} Anna cancel{r.cancelledDriverIds.length === 1 ? '' : 's'}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{r.pickup?.address || '—'}</div>
                        <div className="muted">→ {r.destination?.name || '—'}</div>
                      </td>
                      <td><strong>₹{r.fare}</strong></td>
                      <td>{r.customerName || '—'}<div className="muted mono">{r.customerPhone}</div></td>
                      <td>{r.driverName || '—'}<div className="muted mono">{r.vehicleNumber || '—'}</div></td>
                      <td className="mono">{formatTime(r.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          style={{ fontWeight: 700 }}
                          onClick={() => setSelectedId(r.id)}
                        >
                          Inspect 🔍
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selected && (
        <div
          className="modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={() => { setSelectedId(null); onClearSelected?.(); }}
          style={{ zIndex: 9999 }}
        >
          <div
            className="modal-sheet"
            style={{ maxWidth: 840, maxHeight: '90vh', overflowY: 'auto', border: '1px solid #38bdf8', boxShadow: '0 20px 50px rgba(0,0,0,0.8)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>🚖</span>
                <strong style={{ fontSize: 17, color: '#f8fafc' }}>Ride Inspection · {selected.id}</strong>
                <span className={`badge ${STATUS_CLASS[selected.status] || 'gray'}`}>{STATUS_LABEL[selected.status] || selected.status}</span>
              </div>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setSelectedId(null); onClearSelected?.(); }}>✕ Close</button>
            </div>

            {/* Quick Summary Grid */}
            <div className="detail-grid" style={{ marginBottom: 16 }}>
              <div><strong>FARE</strong>₹{selected.fare} · {selected.paymentMode || 'cash'}</div>
              <div><strong>PICKUP</strong>{selected.pickup?.address}</div>
              <div><strong>DROP</strong>{selected.destination?.name || selected.destination?.address}</div>
              <div>
                <strong>PASSENGER</strong>
                {selected.customerName} ({selected.customerPhone})
                <div className="row-actions" style={{ marginTop: 4 }}>
                  {phoneHref(selected.customerPhone) && (
                    <a className="btn btn-ghost btn-sm" href={phoneHref(selected.customerPhone)}>Call</a>
                  )}
                  {waHref(selected.customerPhone) && (
                    <a className="btn btn-ghost btn-sm" href={waHref(selected.customerPhone)} target="_blank" rel="noreferrer">WA</a>
                  )}
                </div>
              </div>
              <div>
                <strong>ANNA (DRIVER)</strong>
                {selected.driverName || 'None assigned'} · {selected.vehicleNumber || '—'}
                {selected.driverPhone && (
                  <div className="row-actions" style={{ marginTop: 4 }}>
                    <a className="btn btn-ghost btn-sm" href={phoneHref(selected.driverPhone)}>Call</a>
                    <a className="btn btn-ghost btn-sm" href={waHref(selected.driverPhone)} target="_blank" rel="noreferrer">WA</a>
                  </div>
                )}
              </div>
              <div><strong>OTP</strong><span className="mono" style={{ fontSize: 15, fontWeight: 800, color: '#00ff9d' }}>{selected.otp || '—'}</span></div>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: 8, borderBottom: '1px solid #334155', paddingBottom: 8, marginBottom: 16 }}>
              <button
                type="button"
                onClick={() => setActiveTab('timeline')}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: activeTab === 'timeline' ? '#0284c7' : '#1e293b', color: '#fff', border: '1px solid #334155',
                }}
              >
                🔍 Stepped Timeline
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('dispatch')}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: activeTab === 'dispatch' ? '#0284c7' : '#1e293b', color: '#fff', border: '1px solid #334155',
                }}
              >
                🚨 Dispatch Rings
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('events')}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: activeTab === 'events' ? '#0284c7' : '#1e293b', color: '#fff', border: '1px solid #334155',
                }}
              >
                📋 Raw Events ({events.length})
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('chat')}
                style={{
                  padding: '6px 14px', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                  background: activeTab === 'chat' ? '#0284c7' : '#1e293b', color: '#fff', border: '1px solid #334155',
                }}
              >
                💬 Chat ({messages.length})
              </button>
            </div>

            {/* Stepped timeline: server history + current ride state */}
            {activeTab === 'timeline' && <RideTimelineList view={timelineView} formatTime={formatTime} />}

            {/* Dispatch: what this ride's dispatch actually did (no invented ring sizes) */}
            {activeTab === 'dispatch' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ background: '#0f172a', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Dispatch state right now</div>
                  <div style={{ fontSize: 14, color: '#f8fafc', marginTop: 4 }}>
                    {selected.status === 'searching' ? (
                      <>
                        Ring <strong style={{ color: '#38bdf8' }}>{selected.dispatchRing || 1}</strong>
                        {formatRadius(selected.dispatchRadiusM) && ` · within ${formatRadius(selected.dispatchRadiusM)}`}
                      </>
                    ) : (
                      <>Dispatch finished: <strong style={{ color: '#38bdf8' }}>{STATUS_LABEL[selected.status] || selected.status}</strong></>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
                    <span>Annas asked: {Array.isArray(selected.notifiedDriverIds) ? selected.notifiedDriverIds.length : 0}</span>
                    {selected.nearestDriverKm != null && <span>Nearest Anna: {selected.nearestDriverKm} km</span>}
                    {selected.noCaptainsOnline && <span>No Anna online</span>}
                    {selected.searchExhausted && <span>Every ring tried</span>}
                    {selected.autoRedispatchCount > 0 && <span>Auto retries: {selected.autoRedispatchCount}</span>}
                    {selected.keepWaitingCount > 0 && <span>Rider kept waiting ×{selected.keepWaitingCount}</span>}
                    {Array.isArray(selected.cancelledDriverIds) && selected.cancelledDriverIds.length > 0 && (
                      <span style={{ color: '#fca5a5' }}>
                        {selected.cancelledDriverIds.length} Anna{selected.cancelledDriverIds.length === 1 ? '' : 's'} cancelled (not offered this ride again)
                      </span>
                    )}
                  </div>
                </div>

                <div style={{ background: '#1e293b', padding: '12px 16px', borderRadius: 10, border: '1px solid #334155' }}>
                  <div style={{ fontSize: 12, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', marginBottom: 6 }}>What dispatch did</div>
                  {dispatchLog.length === 0 ? (
                    <div className="muted" style={{ fontSize: 13 }}>
                      {timelineView.complete
                        ? 'No dispatch events yet.'
                        : 'No dispatch events were recorded for this ride (it was booked before history logging started).'}
                    </div>
                  ) : dispatchLog.map((step) => (
                    <div key={step.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '6px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <div>
                        <div style={{ fontSize: 13, color: '#f8fafc' }}>{step.icon} {step.title}</div>
                        {step.subtitle && <div style={{ fontSize: 12, color: '#cbd5e1' }}>{step.subtitle}</div>}
                      </div>
                      <span className="mono muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{step.atMs != null ? formatTime(step.atMs) : '—'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw Events */}
            {activeTab === 'events' && (
              <div className="chat-log" style={{ background: '#0f172a', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 10, padding: 12 }}>
                {events.length === 0 ? (
                  <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>No rider-app taps recorded for this ride (calls, Keep waiting, share, SOS). Anna and server events are on the Stepped Timeline.</div>
                ) : (
                  events.map((e) => (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255, 255, 255, 0.05)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span className="badge blue" style={{ fontSize: 11 }}>{e.type}</span>
                        <span style={{ fontSize: 13, color: '#e2e8f0' }}>{e.action || e.rideStatus || JSON.stringify(e)}</span>
                      </div>
                      <span className="mono muted" style={{ fontSize: 11 }}>{formatTime(e.at)}</span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Chat */}
            {activeTab === 'chat' && (
              <div className="chat-log" style={{ background: '#0f172a', border: '1px solid rgba(255, 255, 255, 0.08)', borderRadius: 10, padding: 12 }}>
                {messages.length === 0 ? (
                  <div className="muted">No messages exchanged</div>
                ) : (
                  messages.map((m) => (
                    <div key={m.id} className="chat-line">
                      <span className="mono">{m.senderRole || 'user'}</span>
                      <span>{m.text}</span>
                      <span className="muted mono">{formatTime(m.createdAt)}</span>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Admin Override Actions */}
            {ACTIVE_STATUSES.includes(selected.status) && isSuperAdmin && (
              <div className="row-actions" style={{ marginTop: 16, borderTop: '1px solid #334155', paddingTop: 14 }}>
                <button type="button" className="btn btn-red" disabled={busy === selected.id} onClick={() => cancel(selected)}>
                  Cancel ride
                </button>
                {['driver_assigned', 'arrived'].includes(selected.status) && (
                  <button type="button" className="btn btn-orange" disabled={busy === selected.id} onClick={() => rebroadcast(selected)}>
                    Rebroadcast
                  </button>
                )}
                {['driver_assigned', 'arrived', 'started'].includes(selected.status) && (
                  <button type="button" className="btn btn-orange" disabled={busy === selected.id} onClick={() => forceComplete(selected)}>
                    Force complete
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
